import type { BudgetEntryDTO } from "@zplanem/responses"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../../lib/api-client"
import {
	useDeleteBudgetEntry,
	useSetBudgetEntryStatus,
} from "../../hooks/use-budget"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"

const STATUS_CHIP_CLASS: Record<BudgetEntryDTO["status"], string> = {
	ai_draft: "bg-warning-muted text-warning-muted-foreground",
	confirmed: "bg-success-muted text-success-muted-foreground",
	rejected: "bg-muted text-muted-foreground line-through",
}

/**
 * Budget entry rows (ZPL-224). Per-status actions: an AI draft is accepted
 * (needs an amount first — edit fills it) or rejected; anything can be edited
 * or deleted. Amount shows the original currency plus the ≈ converted figure
 * when the view-context currency differs.
 */
export function BudgetEntryList({
	propertyId,
	entries,
	projectNameById,
	onEdit,
}: {
	propertyId: string
	entries: BudgetEntryDTO[]
	projectNameById: Map<string, string>
	onEdit: (entry: BudgetEntryDTO) => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const tb = t.projects.budget
	const setStatus = useSetBudgetEntryStatus(propertyId)
	const remove = useDeleteBudgetEntry(propertyId)
	const [error, setError] = useState<string | null>(null)
	const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null)

	async function act(fn: () => Promise<unknown>) {
		setError(null)
		try {
			await fn()
		} catch (err) {
			setError(err instanceof ApiError ? err.message : tb.errorSave)
		}
	}

	return (
		<div className="flex flex-col gap-2">
			{error ? (
				<p role="alert" className="text-sm text-destructive">
					{error}
				</p>
			) : null}
			{entries.map((entry) => {
				const converted =
					entry.converted && entry.converted.currency !== entry.currency
						? entry.converted
						: null
				const projectName = entry.projectId
					? (projectNameById.get(entry.projectId) ?? null)
					: null
				const extracting = entry.status === "ai_draft" && entry.amount === null
				return (
					<div
						key={entry.id}
						className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2"
					>
						<div className="flex-1 min-w-40">
							<div className="flex items-center gap-2">
								<span className="font-semibold text-foreground">
									{entry.amount !== null ? (
										`${entry.amount} ${entry.currency}`
									) : (
										<span className="text-muted-foreground font-normal">
											{extracting ? tb.extractionPending : "—"}
										</span>
									)}
								</span>
								{converted ? (
									<span className="text-xs text-muted-foreground">
										≈ {converted.amount} {converted.currency}
									</span>
								) : null}
								<span
									className={`rounded-sm border border-current/25 px-1.5 py-0.5 text-[10px] font-medium ${STATUS_CHIP_CLASS[entry.status]}`}
								>
									{tb.statuses[entry.status]}
								</span>
								{entry.kind ? (
									<span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
										{tb.kinds[entry.kind]}
									</span>
								) : null}
							</div>
							<div className="text-xs text-muted-foreground truncate">
								{[
									entry.vendor,
									entry.description,
									projectName,
									entry.occurredAt
										? new Date(entry.occurredAt).toLocaleDateString(locale)
										: null,
									entry.hasDocument ? tb.proofLabel : null,
								]
									.filter(Boolean)
									.join(" · ")}
							</div>
						</div>

						<div className="flex items-center gap-1">
							{entry.status === "ai_draft" ? (
								<>
									<Button
										size="sm"
										disabled={entry.amount === null || setStatus.isPending}
										onClick={() =>
											act(() =>
												setStatus.mutateAsync({
													entryId: entry.id,
													status: "confirmed",
												}),
											)
										}
									>
										{tb.accept}
									</Button>
									<Button
										size="sm"
										variant="ghost"
										disabled={setStatus.isPending}
										onClick={() =>
											act(() =>
												setStatus.mutateAsync({
													entryId: entry.id,
													status: "rejected",
												}),
											)
										}
									>
										{tb.reject}
									</Button>
								</>
							) : null}
							{entry.status !== "rejected" ? (
								<Button size="sm" variant="ghost" onClick={() => onEdit(entry)}>
									{tb.edit}
								</Button>
							) : null}
							{confirmingDelete === entry.id ? (
								<Button
									size="sm"
									variant="ghost"
									className="text-destructive"
									disabled={remove.isPending}
									onClick={() =>
										act(async () => {
											await remove.mutateAsync(entry.id)
											setConfirmingDelete(null)
										})
									}
								>
									{tb.deleteConfirmBody}
								</Button>
							) : (
								<Button
									size="sm"
									variant="ghost"
									className="text-muted-foreground"
									onClick={() => setConfirmingDelete(entry.id)}
								>
									{tb.delete}
								</Button>
							)}
						</div>
					</div>
				)
			})}
		</div>
	)
}
